import React, { FC, memo } from 'react';
import Button from '../Button';
import { CARTE_GRISE_FEE, REGISTRATION_FEES_VALUES } from 'src/constants/main';
import { HandledComponentError } from '@components/ErrorHandling';
import { IDealerFee } from '../DealerFees/DealerFeesTemplate';
import { SC } from 'src/styles/theme';
import { useFeatureSwitch } from 'src/hooks/useFeatureSwitch';
import useTranslations from '@hooks/useTranslations';
import { useUIDuck } from '@hooks/useUIDuck';
import { useCarDetailsDuck } from '@hooks/useCarDetailsDuck';
import { useGlobalDuck } from '@hooks/useGlobalDuck';
import { Pages } from '../../types/pages';
import { hasMonthlyPriceByType } from '@utils/Price.utils';
import { EMPLOYEE_PRICE_TYPE } from '../../services/filters/filters';
import { useContinueToNextPage } from '@hooks/useContinueToNextPage';
import { GtmData } from '../../types/gtm';
import { useGTM } from '@hooks/useGTM';
import { useExperiment } from '@hooks/useExperiment';
import { PosthogTrigger } from '../../constants/enums';

interface IProps extends SC {
    page: Pages;
    fullWidth?: boolean;
    translationKey?: string;
    htmlId?: string;
    disabled?: boolean;
    onClick?: () => void;
    position?: string;
    dataId?: string;
}

export const isBasketContinueButtonDisabled = (
    isLoading: boolean,
    isBasketPage: boolean,
    carteGriseFee: IDealerFee,
    hasAppliedPostalCode: boolean,
    carteGriseError: boolean,
    isDealerFeesEnabled: boolean
): boolean => {
    if (isDealerFeesEnabled) {
        return (
            isLoading ||
            (isBasketPage &&
                carteGriseFee &&
                carteGriseFee?.priceInclTax > 0 &&
                !hasAppliedPostalCode &&
                !carteGriseError)
        );
    }
    return isLoading;
};

export const ContinueCTAButtonTemplate: FC<IProps> = memo(
    ({ className, page, translationKey, disabled, fullWidth, htmlId, onClick, position, dataId }) => {
        const { t } = useTranslations();
        const { isLoading } = useUIDuck();
        const { currentDeal } = useCarDetailsDuck();
        const { pushTrigger, getPageNameByUrl, getStageViewByUrl } = useExperiment();
        const { carteGriseError } = useGlobalDuck();
        const { onClick: handleContinueToNextPage } = useContinueToNextPage(page, translationKey, position);
        const { pushToDataLayer } = useGTM();

        const selectedFeesChoice = currentDeal?.extraFields?.selectedFeesValue;

        const { hasSelectedPaymentJourney = false } = currentDeal?.extraFields ?? {};

        const { isDealerFeesEnabled } = useFeatureSwitch();

        const carteGriseFee = currentDeal?.fullProductConfiguration?.fees?.find(
            (dealerFee: IDealerFee) => dealerFee.id === CARTE_GRISE_FEE
        );
        const hasAppliedPostalCode = carteGriseFee?.isUsed;

        const isBasketPage = page === Pages.basket || page === Pages.basketV2;

        const isGoToBasketButtonDisabled = isBasketContinueButtonDisabled(
            isLoading,
            isBasketPage,
            carteGriseFee,
            hasAppliedPostalCode,
            carteGriseError,
            isDealerFeesEnabled
        );

        const hasMonthlyPrices = hasMonthlyPriceByType(
            currentDeal?.fullProductConfiguration?.prices,
            EMPLOYEE_PRICE_TYPE
        );

        try {
            return (
                <div className={className}>
                    <Button
                        id={htmlId || 'TESTING_TO_BASKET_BOX'}
                        primary
                        fullWidth
                        data-testid={dataId}
                        className={`${fullWidth ? 'fullWidth' : ''} ${
                            isGoToBasketButtonDisabled ||
                            disabled ||
                            (page === Pages.basketV2 && !hasSelectedPaymentJourney && hasMonthlyPrices) ||
                            (page === Pages.basketV2 &&
                                selectedFeesChoice === REGISTRATION_FEES_VALUES.YES &&
                                !carteGriseFee?.postalCode)
                                ? 'disabled'
                                : ''
                        }`}
                        onClick={() => {
                            if (page === Pages.dealer) {
                                pushToDataLayer({
                                    event: 'uaevent',
                                    eventCategory: 'Content',
                                    eventAction: 'Navigation::FindDealer',
                                    eventLabel: t('configurator.interestedBox1.link.finance'),
                                } as GtmData);
                            }

                            if (page === Pages.basketV2) {
                                pushTrigger(
                                    PosthogTrigger.INTERACTION,
                                    getPageNameByUrl(),
                                    null,
                                    null,
                                    null,
                                    null,
                                    null,
                                    null,
                                    null,
                                    null,
                                    null,
                                    'hand_off_to_checkout',
                                    null,
                                    getStageViewByUrl()
                                );
                            }

                            handleContinueToNextPage();
                            onClick?.();
                        }}
                        disabled={
                            isGoToBasketButtonDisabled ||
                            disabled ||
                            (page === Pages.basketV2 && !hasSelectedPaymentJourney && hasMonthlyPrices) ||
                            (page === Pages.basketV2 &&
                                selectedFeesChoice === REGISTRATION_FEES_VALUES.YES &&
                                !carteGriseFee?.postalCode)
                        }
                    >
                        <span>{t(translationKey || 'configurator.interestedBox1.link.finance')}</span>
                    </Button>
                </div>
            );
        } catch (e: any) {
            return <HandledComponentError error={e} />;
        }
    }
);

ContinueCTAButtonTemplate.displayName = 'ContinueCTAButtonTemplate';
